import { get, post } from './client';
import type {
  AdminInfo,
  Art,
  Card,
  Collect,
  Comment,
  DashboardStats,
  DbBackup,
  DbTable,
  Gbook,
  Group,
  Link,
  LoginResult,
  Order,
  PageParams,
  PageResult,
  Player,
  Template,
  Topic,
  User,
  Vod,
  VodType,
} from '@/types';

// ============ 鉴权 ============

/** 管理员登录 */
export const loginApi = (data: { admin_name: string; admin_pwd: string }) =>
  post<LoginResult>('/admin/auth/login', data);

/** 管理员退出 */
export const logoutApi = () => post('/admin/auth/logout');

// ============ 仪表盘 ============

/** 获取仪表盘统计数据 */
export const getDashboardApi = () => get<DashboardStats>('/admin/dashboard');

// ============ 视频管理 ============

export interface VodListParams extends PageParams {
  type?: number;
  level?: number;
  status?: number | string;
  isend?: number | string;
  area?: string;
  lang?: string;
  year?: string;
  order?: string;
}

/** 视频列表 */
export const getVodListApi = (params: VodListParams) =>
  get<PageResult<Vod>>('/admin/vod/list', params as Record<string, unknown>);

/** 视频详情 */
export const getVodDetailApi = (id: number) => get<Vod>('/admin/vod/detail', { id });

/** 新建视频 */
export const createVodApi = (data: Partial<Vod>) => post('/admin/vod/create', data as Record<string, unknown>);

/** 编辑视频 */
export const updateVodApi = (data: Partial<Vod>) => post('/admin/vod/update', data as Record<string, unknown>);

/** 删除视频 */
export const deleteVodApi = (ids: string) => post('/admin/vod/delete', { ids });

/** 视频批量操作 */
export const batchVodApi = (data: Record<string, unknown>) => post('/admin/vod/batch', data);

// ============ 分类管理 ============

/** 分类列表（树形） */
export const getTypeListApi = () => get<VodType[]>('/admin/type/list');

/** 新建分类 */
export const createTypeApi = (data: Partial<VodType>) => post('/admin/type/create', data as Record<string, unknown>);

/** 编辑分类 */
export const updateTypeApi = (data: Partial<VodType>) => post('/admin/type/update', data as Record<string, unknown>);

/** 删除分类 */
export const deleteTypeApi = (ids: string) => post('/admin/type/delete', { ids });

// ============ 用户管理 ============

export interface UserListParams extends PageParams {
  status?: number | string;
  group?: number;
}

/** 用户列表 */
export const getUserListApi = (params: UserListParams) =>
  get<PageResult<User>>('/admin/user/list', params as Record<string, unknown>);

/** 创建用户 */
export const createUserApi = (data: Partial<User>) => post('/admin/user/create', data as Record<string, unknown>);

/** 更新用户 */
export const updateUserApi = (data: Partial<User>) => post('/admin/user/update', data as Record<string, unknown>);

/** 删除用户 */
export const deleteUserApi = (ids: string) => post('/admin/user/delete', { ids });

/** 用户组列表 */
export const getGroupListApi = () => get<Group[]>('/admin/group/list');

// ============ 文章管理 ============

/** 文章列表 */
export const getArtListApi = (params: PageParams) =>
  get<PageResult<Art>>('/admin/art/list', params as Record<string, unknown>);

/** 新建文章 */
export const createArtApi = (data: Partial<Art>) => post('/admin/art/create', data as Record<string, unknown>);

/** 编辑文章 */
export const updateArtApi = (data: Partial<Art>) => post('/admin/art/update', data as Record<string, unknown>);

/** 删除文章 */
export const deleteArtApi = (ids: string) => post('/admin/art/delete', { ids });

// ============ 专题管理 ============

/** 专题列表 */
export const getTopicListApi = (params: PageParams) =>
  get<PageResult<Topic>>('/admin/topic/list', params as Record<string, unknown>);

/** 新建专题 */
export const createTopicApi = (data: Partial<Topic>) => post('/admin/topic/create', data as Record<string, unknown>);

/** 编辑专题 */
export const updateTopicApi = (data: Partial<Topic>) => post('/admin/topic/update', data as Record<string, unknown>);

/** 删除专题 */
export const deleteTopicApi = (ids: string) => post('/admin/topic/delete', { ids });

// ============ 采集管理 ============

/** 采集接口列表 */
export const getCollectListApi = (params: PageParams) =>
  get<PageResult<Collect>>('/admin/collect/list', params as Record<string, unknown>);

/** 新建采集接口 */
export const createCollectApi = (data: Partial<Collect>) =>
  post('/admin/collect/create', data as Record<string, unknown>);

/** 编辑采集接口 */
export const updateCollectApi = (data: Partial<Collect>) =>
  post('/admin/collect/update', data as Record<string, unknown>);

/** 删除采集接口 */
export const deleteCollectApi = (ids: string) => post('/admin/collect/delete', { ids });

/** 采集接口分类绑定 */
export const bindCollectApi = (data: { ids: string; col: string; val: number }) =>
  post('/admin/collect/bind', data);

/** 执行采集 */
export const runCollectApi = (data: Record<string, unknown>) => post('/admin/collect/run', data);

// ============ 卡密管理 ============

/** 卡密列表 */
export const getCardListApi = (params: PageParams) =>
  get<PageResult<Card>>('/admin/card/list', params as Record<string, unknown>);

/** 生成卡密 */
export const createCardApi = (data: {
  card_points: number;
  card_gold: number;
  card_num: number;
}) => post('/admin/card/create', data);

/** 删除卡密 */
export const deleteCardApi = (ids: string) => post('/admin/card/delete', { ids });

// ============ 订单管理 ============

export interface OrderListParams extends PageParams {
  status?: number | string;
}

/** 订单列表 */
export const getOrderListApi = (params: OrderListParams) =>
  get<PageResult<Order>>('/admin/order/list', params as Record<string, unknown>);

// ============ 广告管理 ============

/** 广告列表 */
export const getAdListApi = (params: PageParams) =>
  get<PageResult<Ad>>('/admin/ad/list', params as Record<string, unknown>);

/** 新建广告 */
export const createAdApi = (data: Partial<Ad>) => post('/admin/ad/create', data as Record<string, unknown>);

/** 编辑广告 */
export const updateAdApi = (data: Partial<Ad>) => post('/admin/ad/update', data as Record<string, unknown>);

/** 删除广告 */
export const deleteAdApi = (ids: string) => post('/admin/ad/delete', { ids });

// ============ 评论管理 ============

/** 评论列表 */
export const getCommentListApi = (params: PageParams) =>
  get<PageResult<Comment>>('/admin/comment/list', params as Record<string, unknown>);

/** 删除评论 */
export const deleteCommentApi = (ids: string) => post('/admin/comment/delete', { ids });

/** 审核评论 */
export const auditCommentApi = (data: { ids: string; status: number }) =>
  post('/admin/comment/audit', data);

// ============ 留言管理 ============

/** 留言列表 */
export const getGbookListApi = (params: PageParams) =>
  get<PageResult<Gbook>>('/admin/gbook/list', params as Record<string, unknown>);

/** 删除留言 */
export const deleteGbookApi = (ids: string) => post('/admin/gbook/delete', { ids });

/** 回复留言 */
export const replyGbookApi = (data: { id: number; reply: string }) => post('/admin/gbook/reply', data);

// ============ 系统设置 ============

/** 获取系统配置 */
export const getSystemConfigApi = () => get<Record<string, any>>('/admin/system/config');

/** 保存系统配置 */
export const saveSystemConfigApi = (data: Record<string, any>) => post('/admin/system/save', data);

// ============ 播放器管理 ============

/** 播放器列表 */
export const getPlayerListApi = () => get<Player[]>('/admin/player/list');

/** 更新播放器 */
export const updatePlayerApi = (data: Partial<Player> & { from: string }) =>
  post('/admin/player/update', data as Record<string, unknown>);

// ============ 数据库管理 ============

/** 数据库表列表 */
export const getDbTablesApi = () => get<DbTable[]>('/admin/database/tables');

/** 备份数据库 */
export const backupDbApi = (tables?: string[]) =>
  post('/admin/database/backup', { tables });

/** 优化数据库 */
export const optimizeDbApi = (tables: string[]) => post('/admin/database/optimize', { tables });

// ============ 模板管理 ============

/** 模板列表 */
export const getTemplateListApi = () => get<Template[]>('/admin/template/list');

/** 启用模板 */
export const saveTemplateApi = (data: { dir: string }) => post('/admin/template/save', data);

// ============ 友情链接 ============

/** 友情链接列表 */
export const getLinkListApi = (params: PageParams) =>
  get<PageResult<Link>>('/admin/link/list', params as Record<string, unknown>);

/** 新建友情链接 */
export const createLinkApi = (data: Partial<Link>) => post('/admin/link/create', data as Record<string, unknown>);

/** 编辑友情链接 */
export const updateLinkApi = (data: Partial<Link>) => post('/admin/link/update', data as Record<string, unknown>);

/** 删除友情链接 */
export const deleteLinkApi = (ids: string) => post('/admin/link/delete', { ids });

// 重新导出管理员信息类型，便于页面使用
export type { AdminInfo };
